import React, { useEffect } from 'react'
import { Flex } from 'rebass';
import { Box } from 'rebass';
import { Text } from 'rebass';
import { createGlobalStyle } from 'styled-components';
import Badge from './components/Badge';
import Button from './components/Button';
import Message from './components/Message';
import Icon from './components/Icon';
import CodeBlock from './components/CodeBlock';

import Prism from "./prism";
import PrismDark from './prism-dark';
import PrismLight from './prism-light';
import useLocalStorage from './hooks/useLocalStorage';

import { palettes, replaceAlias } from './ThemeSwitcher';

const getContrastValue = (foreground, background) => {
  let backgroundColorKey = background || 'background';
  let backgroundColor = getComputedStyle(document.body).getPropertyValue(`--${backgroundColorKey}`).trim();
  const contrastAgainstBackground = chroma.contrast(computedValue, backgroundColor);

  return contrastAgainstBackground;
};

const HR = () => (
  <Box
    as="hr"
    css={`
      border: 0;
      height: 1px;
      background: var(--borderNeutralMutedAlpha);
    `}
  />
);

const ExampleWrapper = ({ label, description, components, code }) => (
  <Box
    as="section"
    mb={5}
    // css={`
    //   border-radius: 8px;
    //   border: 1px solid var(--borderNeutralMutedAlpha);
    //   padding: 16px;
    //   margin-bottom: 16px;
    // `}
  >
    <Text
      mb={2}
      fontSize={0}
      fontWeight="bold"
      color="var(--textNeutralSubtle)"
      css={`
        text-transform: uppercase;
      `}
    >
      {label}
    </Text>
    {description && <Text
      mb={2}
      fontSize={0}
      color="var(--textNeutralSubtle)"
    >
      {description}
    </Text>}
    <Box
      css={`
        border-radius: 8px;
        border: 1px dashed var(--borderNeutralMutedAlpha);
        padding: 16px;
        /* border: inset 0 0 5px var(--borderNeutralMutedAlpha); */
      `}
    >
      {components}
    </Box>
    {code}
  </Box>
);

const capitalize = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const GlobalStyle = createGlobalStyle`
  ${props => props.dark ? PrismDark : PrismLight}
`;

const ColorBlock = ({ iconColor, textColor, backgroundColor, label }) => {
  return (
    <Box
      mr={3}
      mb={3}
      css={`
        display: inline-block;
        
      `}
    >
      <Box
        bg={`var(--${backgroundColor})`}
        css={`
          display: flex;
          align-items: center;
          justify-content: center;

          border: 1px solid var(--borderNeutralMutedAlpha);
          border-radius: 8px;
          height: 80px;
          width: 150px;
        `}
      >
        {textColor && <Text
          fontWeight="600"
          fontSize={5}
          color={`var(--text${textColor})`}
        >Aa</Text>}
        {iconColor && <Icon
          icon="face-smile-relaxed"
          fontSize={5}
          color={`var(--icon${iconColor})`}
        />}
      </Box>
      <Text
        as="code"
        mt={2}
        fontSize={0}
        color="var(--textNeutral)"
        fontFamily="Roboto Mono"
        css={`
          display: inline-block;
        `}
      >
        {label}
      </Text>
    </Box>
  )
};

const Examples = ({ palette, children, alias, aliasLowerCase }) => (
  <div>
    <Text mt={3} mb={3} fontWeight="600">Examples</Text>
    {children}

    <ExampleWrapper
      label="Buttons"
      components={(
        <div>
          <Button color={aliasLowerCase}>
            Click me
          </Button>
          <Button color={aliasLowerCase} variant="solid" ml={2}>
            Click me
          </Button>
        </div>
      )}
      code={(
        <CodeBlock>
        {palette === 'Gray' ? `// default color is "${aliasLowerCase}"
<Button>
  Click me
</Button>

// default color is "${aliasLowerCase}"
<Button variant="solid">
  Click me
</Button>` : `<Button color="${aliasLowerCase}">
  Click me
</Button>

<Button color="${aliasLowerCase}" variant="solid">
  Click me
</Button>`}
        </CodeBlock>
      )}
    />

    <ExampleWrapper
      label="Message"
      components={(
        <Message
          color={aliasLowerCase}
          headingText="This is a message"
          icon="info-circle"
        >
          Please read me
        </Message>
      )}
      code={(
        <CodeBlock>
        {palette === 'Gray' ? `// default color is "${aliasLowerCase}"
<Message
  icon="info-circle"
  headingText="This is a message"
> 
  Please read me
</Message>` : `<Message
  color="${aliasLowerCase}"
  icon="info-circle"
  headingText="This is a message"
> 
  Please read me
</Message>`}
        </CodeBlock>
      )}
    />
    <ExampleWrapper
      label="Badge"
      components={(
        <Badge icon="check" color={aliasLowerCase}>
          Confirmed
        </Badge>
      )}
      code={(
        <CodeBlock>
        {palette === 'Gray' ? `// default color is "${aliasLowerCase}"
<Badge icon="check">
  Confirmed
</Badge>` : `<Badge icon="check" color="${aliasLowerCase}">
Confirmed
</Badge>`}
        </CodeBlock>
      )}
    />
  </div>
);

const System = ({ selectedTheme }) => {
  const useSemanticAlias = true;
  const [showExamples, setShowExamples] = React.useState(false);
  const [selectedPalette, setSelectedPalette] = React.useState('');
  const headerRef = React.useRef();
  const [isSticking, setIsSticking] = React.useState(false);

  useEffect(() => {
    Prism.highlightAll();
  }, [useSemanticAlias, selectedPalette, selectedTheme, showExamples]);

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }
    const handleScroll = () => {
      const headerRefTop = headerRef.current.getBoundingClientRect().top;
      const sticking = headerRefTop <= 0;
      setIsSticking(sticking);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, [headerRef]);

  return (
    <Box>
      <GlobalStyle dark={selectedTheme && selectedTheme.theme === 'dark'} />
      <Flex
        ref={headerRef}
        alignItems="center"
        justifyContent="space-between"
        py={2}
        mb={4}
        css={`
          background: var(--background);
          border-bottom: 1px solid transparent;
          ${isSticking && `border-bottom-color: var(--borderNeutralMutedAlpha);`}
          transition-duration: .2s;
          position: sticky;
          top: -1px;
          z-index: 1;
        `}
      >
        <Flex alignItems="center">
          {/* <Box as="label" css="display: block;" mr={4}>
            <Box
              as="input"
              type="checkbox"
              checked={useSemanticAlias}
              onChange={() => setUseSemanticAlias(!useSemanticAlias)}
            />
            <Text fontSize={1} ml={2} css="display: inline;">
              Use semantic alias
            </Text>
          </Box> */}
          <Box as="label" css="display: block;">
            <Box
              as="input"
              type="checkbox"
              checked={showExamples}
              onChange={() => setShowExamples(!showExamples)}
            />
            <Text fontSize={1} ml={2} css="display: inline;">
              Show examples
            </Text>
          </Box>
        </Flex>
        {/* <Box
          as="select"
          onChange={e => setSelectedPalette(e.target.value)}
          value={selectedPalette}
        >
          <option value="">All palettes</option>
          {palettes({ alias: useSemanticAlias }).map(colorKey => (
            <option key={colorKey} value={colorKey}>
              {capitalize(replaceAlias(colorKey, useSemanticAlias))}
            </option>
          ))}
        </Box> */}
      </Flex>

      {palettes({ active: useSemanticAlias }).filter(s => selectedPalette ? s === selectedPalette : true).map(p => {
        const palette = p;
        const alias = replaceAlias(p, useSemanticAlias);
        const aliasLowerCase = alias.toLowerCase();

        return palette === 'Gray' ? (
        <Box mb={5} key={p}>
          <Text as="h2" my={3}>
            Neutral
            {/* {useSemanticAlias && ` (${capitalize(alias)})`} */}
          </Text>
          <HR />
          <br />

          <Text fontSize={0} fontWeight="700" mb={2} color="var(--text-tertiary)">Text</Text>
          <ColorBlock
            label="textNeutral"
            textColor="Neutral"
          />
          <ColorBlock
            label="textNeutralMuted"
            textColor="NeutralMuted"
          />
          <ColorBlock
            label="textNeutralSubtle"
            textColor="NeutralSubtle"
          />
          <ColorBlock
            label={replaceAlias('textOnBackgroundNeutral', useSemanticAlias)}
            textColor="OnBackgroundNeutral"
            backgroundColor="backgroundNeutral"
          />
          <br />
          <br />
          <Text fontSize={0} fontWeight="700" mb={2} color="var(--text-tertiary)">Icon</Text>
          <ColorBlock
            label="iconNeutral"
            iconColor="Neutral"
          />
          <ColorBlock
            label="iconInactive"
            iconColor="Inactive"
          />
          <br />
          <br />
          <Text fontSize={0} fontWeight="700" mb={2} color="var(--text-tertiary)">Background</Text>
          <ColorBlock
            label="background"
            backgroundColor="background"
          />
           {/* <ColorBlock
            label="surface"
            backgroundColor="background-surface"
          /> */}
          <ColorBlock
            label={replaceAlias('backgroundNeutralEmphasis', useSemanticAlias)}
            backgroundColor="backgroundNeutralEmphasis"
          />
          <ColorBlock
            label={replaceAlias('backgroundNeutral', useSemanticAlias)}
            backgroundColor="backgroundNeutral"
          />
          <ColorBlock
            label={replaceAlias('backgroundNeutralMuted', useSemanticAlias)}
            backgroundColor="backgroundNeutralMuted"
          />
          <ColorBlock
            label={replaceAlias('backgroundNeutralSubtle', useSemanticAlias)}
            backgroundColor="backgroundNeutralSubtle"
          />

          {showExamples && <Examples palette={palette} p={p} alias={alias} aliasLowerCase={aliasLowerCase}>
          <ExampleWrapper
            label="Text"
            description="For text components, you can just pass name of the color token without the `text` prefix."
            components={(
              <div>
                <Text as="h3" color={`var(--textNeutral)`} mb={2}>Default heading</Text>
                <Text color={`var(--textNeutral)`}>Default text</Text>
                <Text color={`var(--textNeutralMuted)`}>Muted text</Text>
                <Text color={`var(--textNeutralSubtle)`}>Subtle text</Text>
              </div>
            )}
            code={(
              <CodeBlock>
      {`// default color is "neutral"
<Heading>
  Default heading
</Heading>

// default color is "neutral"
<Text>
  Default text
</Text>

<Text color="neutralMuted">
  Muted text
</Text>

<Text color="neutralSubtle">
  Subtle text
</Text>`}
              </CodeBlock>
            )}
          />
          </Examples>}
        </Box>
        ) : (
        <Box mb={5} key={p}>
          <Text as="h2" my={3}>
            {alias}
          </Text>
          <HR />
          <br />
          <Text fontSize={0} fontWeight="700" mb={2} color="var(--text-tertiary)">Text</Text>
          <ColorBlock
            label={`text${alias}`}
            textColor={alias}
          />
          <ColorBlock
            label={`textOnBackground${alias}`}
            textColor={`OnBackground${alias}`}
            backgroundColor={`background${alias}`}
          />
          <br />
          <br />
          <Text fontSize={0} fontWeight="700" mb={2} color="var(--text-tertiary)">Icon</Text>
          <ColorBlock
            label={`icon${alias}`}
            iconColor={alias}
          />
          <br />
          <br />
          <Text fontSize={0} fontWeight="700" mb={2} color="var(--text-tertiary)">Background</Text>
          <ColorBlock
            label={`background${alias}`}
            backgroundColor={`background${alias}`}
          />
          <ColorBlock
            label={`background${alias}Muted`}
            backgroundColor={`background${alias}Muted`}
          />
          <ColorBlock
            label={`background${alias}Subtle`}
            backgroundColor={`background${alias}Subtle`}
          />

          {showExamples && <Examples palette={palette} alias={alias} aliasLowerCase={aliasLowerCase}>
          <ExampleWrapper
            label="Text"
            components={(
              <div>
                <Text as="h3" color={`var(--text${alias})`} mb={2}>Heading</Text>
                <Text color={`var(--text${alias})`}>Body text</Text>
              </div>
            )}
            code={(
            <CodeBlock>
    {`<Heading color="${alias.toLowerCase()}">
  Some heading
</Heading>

<Text color="${alias.toLowerCase()}">
  Tertiary text
</Text>`}
            </CodeBlock>
            )}
            />
          </Examples>}
        </Box>
      );
      })}
    </Box>
  )
}

export default System;